import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { mixpanelEvent } from "@/lib/mixpanels/mixpanel";
import { CASH_INDEX_PAGE } from "@/settings/constant";
import { Button, DialogButton, DialogTitle, Divider, Icon, Typography } from "@bookips/sds";
import Link from "next/link";
import { useEffect } from "react";
import useCheckoutStore from "src/store/checkout.store";
import { useDialogStore } from "src/store/useDialogStore";
import { shallow } from "zustand/shallow";
import { RadiusBoxStyle } from ".";
import useGetCashBalance from "../wallet/hooks/useGetCashBalance";
import useGetPointBalance from "../wallet/hooks/useGetPointBalance";
import CashPointUseRow, { CURRENCY_UNIT, roundDownToUnit } from "./CashPointUseRow";
import useAlwaysUseCash from "./hooks/useAlwaysUseCash";
const CashPointCard = (param)=>{
    let { orderType  } = param;
    const { open , close  } = useDialogStore();
    const { updateUsedCash , updateUsedPoint , priceInfo , updateMyCashBalance , updateMyPointBalance , myPointBalance , myCashBalance , isAlwaysUseCash , updateIsAlwaysUseCash  } = useCheckoutStore((state)=>({
            updateUsedCash: state.updateUsedCash,
            updateUsedPoint: state.updateUsedPoint,
            priceInfo: state.priceInfo,
            updateMyCashBalance: state.updateMyCashBalance,
            updateMyPointBalance: state.updateMyPointBalance,
            myPointBalance: state.myPointBalance,
            myCashBalance: state.myCashBalance,
            isAlwaysUseCash: state.isAlwaysUseCash,
            updateIsAlwaysUseCash: state.updateIsAlwaysUseCash
        }), shallow);
    // 항상 전액 사용 로컬 스토리지 관리 훅
    const { getValue: getAlwaysUseValue  } = useAlwaysUseCash();
    const { data: cashBalance  } = useGetCashBalance();
    const { data: pointBalance  } = useGetPointBalance();
    const totalAmount = priceInfo.originalPrice - priceInfo.productDiscount + (priceInfo.deliveryFee || 0) + (priceInfo.remoteAreaFee || 0) - (priceInfo.deliveryDiscount || 0);
    var _cashBalance_totalBalance, _ref;
    // 비동기로 로드되기 전에는 store의 값을 사용 (fallback)
    const availableCashBalance = (_ref = (_cashBalance_totalBalance = cashBalance === null || cashBalance === void 0 ? void 0 : cashBalance.totalBalance) !== null && _cashBalance_totalBalance !== void 0 ? _cashBalance_totalBalance : myCashBalance) !== null && _ref !== void 0 ? _ref : 0;
    var _pointBalance_totalBalance, _ref1;
    const availablePointBalance = (_ref1 = (_pointBalance_totalBalance = pointBalance === null || pointBalance === void 0 ? void 0 : pointBalance.totalBalance) !== null && _pointBalance_totalBalance !== void 0 ? _pointBalance_totalBalance : myPointBalance) !== null && _ref1 !== void 0 ? _ref1 : 0;
    // 캐시/포인트 최대 사용 가능 금액 (서로 독립적으로 계산)
    const maxCashUsable = Math.min(availableCashBalance, totalAmount);
    const maxPointUsable = Math.max(0, Math.min(totalAmount - (priceInfo.unusedPointPrice || 0), availablePointBalance));
    const handleInfoClick = ()=>{
        // mx-323
        mixpanelEvent("Cash/Point Info Icon Clicked", {
            "Order Type": orderType
        });
        open({
            id: "cash-info",
            type: "custom",
            props: {
                title: /*#__PURE__*/ _jsx(DialogTitle, {
                    title: "캐시/포인트",
                    className: "mt-7 mx-7 max-sm:mx-5 max-sm:mt-5"
                }),
                children: /*#__PURE__*/ _jsx(CashPointUseInfo, {}),
                footer: /*#__PURE__*/ _jsx(DialogButton, {
                    confirmText: "닫기",
                    onConfirm: ()=>close("cash-info"),
                    classes: {
                        confirmButton: "max-sm:w-full",
                        root: "max-sm:px-5 max-sm:pb-5"
                    }
                }),
                contentProps: {
                    onInteractOutside: ()=>{
                        close("cash-info");
                    }
                },
                classes: {
                    contentWrapper: "max-sm:w-[calc(100vw-40px)]"
                }
            }
        });
    };
    const handleChangePointUsed = (amount)=>{
        if (amount > maxPointUsable) return;
        // 캐시 + 포인트가 totalAmount를 초과하면 캐시를 줄여줌 (10원 단위 절삭)
        const newCashAmount = roundDownToUnit(Math.max(0, totalAmount - amount));
        if ((priceInfo.usedCash || 0) > newCashAmount) {
            updateUsedCash(newCashAmount);
        }
        updateUsedPoint(amount);
    };
    const handleChangeCashUsed = (amount)=>{
        if (amount > maxCashUsable) return;
        // 캐시 + 포인트가 totalAmount를 초과하면 포인트를 줄여줌 (10원 단위 절삭)
        const maxPointAfterCash = roundDownToUnit(Math.max(0, totalAmount - amount - (priceInfo.unusedPointPrice || 0)));
        if ((priceInfo.usedPoint || 0) > maxPointAfterCash) {
            updateUsedPoint(maxPointAfterCash);
        }
        updateUsedCash(amount);
    };
    // 항상 전액 사용 체크박스 변경 핸들러 (store에만 저장, 결제 성공 시 로컬스토리지에 저장)
    const handleAlwaysUseChange = (checked)=>{
        updateIsAlwaysUseCash(checked);
    };
    const isCashUnavailable = ()=>availableCashBalance < CURRENCY_UNIT;
    // 포인트 사용 불가 여부 (입력창 비활성화용)
    const isPointUnavailable = ()=>{
        // 1. 보유 포인트 부족 (10원 미만)
        if (availablePointBalance < CURRENCY_UNIT) return true;
        // 2. 모든 상품이 포인트 사용 불가
        if (priceInfo.isUnusedPoint) return true;
        // 3. 포인트 사용 가능한 금액이 없음 (포인트 불가 상품만 있는 경우)
        if (maxPointUsable <= 0 && (priceInfo.unusedPointPrice || 0) > 0) return true;
        // 4. 애초에 결제할 금액이 없음 (캐시/포인트로 0을 만든 경우는 되돌릴 수 있어야 하므로 제외)
        if (priceInfo.finalPrice === 0 && (priceInfo.usedCash || 0) === 0 && (priceInfo.usedPoint || 0) === 0) return true;
        return false;
    };
    // 메시지 표시용: 모든 상품이 포인트 불가 상품일 때만 표시
    const showPointUnavailableMessage = priceInfo.isUnusedPoint || maxPointUsable <= 0 && (priceInfo.unusedPointPrice || 0) > 0;
    // 캐시/포인트 잔액 업데이트
    useEffect(()=>{
        if (!cashBalance || !pointBalance) return;
        updateMyCashBalance((cashBalance === null || cashBalance === void 0 ? void 0 : cashBalance.totalBalance) || 0);
        updateMyPointBalance((pointBalance === null || pointBalance === void 0 ? void 0 : pointBalance.totalBalance) || 0);
    }, [
        cashBalance,
        pointBalance
    ]);
    // 초기 로드 시 로컬 스토리지에서 값 불러오기
    useEffect(()=>{
        const storedValue = getAlwaysUseValue();
        updateIsAlwaysUseCash(storedValue);
    }, []) // 마운트 시 한 번만 실행
    ;
    // 언마운트 시 사용 금액 및 항상 전액 사용 체크 초기화
    useEffect(()=>{
        return ()=>{
            updateUsedCash(0);
            updateUsedPoint(0);
            updateIsAlwaysUseCash(false);
        };
    }, []);
    return /*#__PURE__*/ _jsxs(RadiusBoxStyle, {
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center justify-between",
                children: [
                    /*#__PURE__*/ _jsxs(Typography, {
                        variant: "h6",
                        className: "flex items-center gap-1.5",
                        children: [
                            "캐시/포인트",
                            /*#__PURE__*/ _jsx("p", {
                                role: "button",
                                onClick: handleInfoClick,
                                className: "hover:cursor-pointer",
                                children: /*#__PURE__*/ _jsx(Icon.Info_2px, {
                                    className: "size-4"
                                })
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(Link, {
                        href: CASH_INDEX_PAGE,
                        target: "_blank",
                        onClick: ()=>{
                            // mx-322
                            mixpanelEvent("Cash Recharge Button Clicked", {
                                "Order Type": orderType
                            });
                        },
                        children: /*#__PURE__*/ _jsx(Button, {
                            variant: "stroke",
                            color: "primary",
                            className: "w-fit",
                            size: "sm",
                            children: "캐시 충전"
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-col gap-4",
                children: [
                    /*#__PURE__*/ _jsx(CashPointUseRow, {
                        type: "cash",
                        title: "보유 캐시",
                        orderType: orderType,
                        value: availableCashBalance || priceInfo.usedCash,
                        maxValue: maxCashUsable,
                        placeholder: isCashUnavailable() ? "0원" : "사용 금액 입력 (10원 단위)",
                        buttonText: "전액사용",
                        isNotAvailablePoint: isCashUnavailable(),
                        options: {
                            isAlwaysUseCheckbox: true,
                            isAlwaysUsedChecked: isAlwaysUseCash,
                            onAlwaysUseChange: handleAlwaysUseChange
                        },
                        onValueChange: handleChangeCashUsed,
                        currentValue: priceInfo.usedCash,
                        myOwnValue: availableCashBalance,
                        valueColor: "text-primary-main"
                    }),
                    /*#__PURE__*/ _jsx(Divider, {}),
                    /*#__PURE__*/ _jsx(CashPointUseRow, {
                        type: "point",
                        title: "보유 포인트",
                        orderType: orderType,
                        value: availablePointBalance || priceInfo.usedPoint,
                        maxValue: maxPointUsable,
                        placeholder: isPointUnavailable() ? "0원" : "사용 금액 입력 (10원 단위)",
                        buttonText: "전액사용",
                        isNotAvailablePoint: isPointUnavailable(),
                        onlyUnusableProducts: showPointUnavailableMessage,
                        onValueChange: handleChangePointUsed,
                        currentValue: priceInfo.usedPoint,
                        myOwnValue: availablePointBalance,
                        valueColor: "text-strong"
                    })
                ]
            })
        ]
    });
};
// 해당 내부에서 사용하는 캐시,포인트 정보 다이얼로그 컴포넌트
const CashPointUseInfo = ()=>{
    return /*#__PURE__*/ _jsxs("section", {
        className: "flex flex-col px-7 gap-7 max-sm:px-5",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-col gap-3",
                children: [
                    /*#__PURE__*/ _jsx(Typography, {
                        variant: "subtitle1",
                        className: "text-gray-700",
                        children: "캐시"
                    }),
                    /*#__PURE__*/ _jsx("ul", {
                        className: "list-disc list-outside ml-2",
                        children: /*#__PURE__*/ _jsx("li", {
                            className: "ml-2",
                            children: "10원 단위로 사용할 수 있어요."
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-col gap-3",
                children: [
                    /*#__PURE__*/ _jsx(Typography, {
                        variant: "subtitle1",
                        className: "text-gray-700",
                        children: "포인트"
                    }),
                    /*#__PURE__*/ _jsxs("ul", {
                        className: "list-disc list-outside ml-2",
                        children: [
                            /*#__PURE__*/ _jsx("li", {
                                className: "ml-2",
                                children: "10원 단위로 사용할 수 있어요."
                            }),
                            /*#__PURE__*/ _jsx("li", {
                                className: "ml-2",
                                children: "‘포인트 사용 불가 상품'으로 표시된 상품은 결제 시, 포인트를 사용할 수 없어요."
                            })
                        ]
                    })
                ]
            })
        ]
    });
};
export default CashPointCard;
